'use client';

import type { CmsShellViewerMode } from '@/cms/chrome/shell-viewer-mode';
import type { AuthSessionHintUser } from '@/hooks/auth/use-has-auth-session-hint';
import { useAuthSessionHint } from '@/hooks/auth/use-has-auth-session-hint';
import {
  type AuthSessionHintDocumentState,
  syncAuthSessionHintDocumentState,
} from '@/lib/auth/auth-session-hint-document';
import {
  type AuthSessionTransitionState,
  markAuthSessionTransitionValidating,
  readAuthSessionTransitionDirection,
  useAuthSessionObserver,
} from '@/lib/auth/auth-session-observer';
import { useAuthSession } from '@/lib/auth/auth-session-provider';
import { useAuthSessionObserverPublisher } from '@/lib/auth/use-auth-session-observer-publisher';
import type { ReactNode } from 'react';
import { useEffect, useLayoutEffect, useRef } from 'react';

type CmsShellViewerStateSnapshot = {
  currentUser: AuthSessionHintUser | null;
  hasAuthSessionHint: boolean;
  isAuthenticated: boolean;
  isPending: boolean;
  mode: CmsShellViewerMode;
  shouldAssumeAuthenticated: boolean;
};

const PREVIEW_AUTHENTICATED_USER: AuthSessionHintUser = {
  id: 'cms-block-preview-user',
  name: 'Preview User',
  email: 'preview@imgenhancer.app',
  image: null,
  role: 'user',
  preferredLocale: 'en',
};

const useBrowserLayoutEffect =
  typeof window === 'undefined' ? useEffect : useLayoutEffect;

function useSyncAuthSessionHintDocumentState(
  state: AuthSessionHintDocumentState | null
) {
  useBrowserLayoutEffect(() => {
    if (state) {
      syncAuthSessionHintDocumentState(state);
    }
  }, [state]);
}

function useMarkExternalSignInForValidation(
  transition: AuthSessionTransitionState | null
) {
  const pageWasHiddenRef = useRef(false);

  useEffect(() => {
    if (!transition || transition.direction !== 'sign-in') {
      return undefined;
    }

    const handlePageHide = () => {
      pageWasHiddenRef.current = true;
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted && !pageWasHiddenRef.current) {
        return;
      }

      pageWasHiddenRef.current = false;
      markAuthSessionTransitionValidating({ id: transition.id });
    };

    window.addEventListener('pagehide', handlePageHide);
    window.addEventListener('pageshow', handlePageShow);
    return () => {
      window.removeEventListener('pagehide', handlePageHide);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [transition]);
}

export function CmsShellViewerState({
  children,
  mode = 'live',
}: {
  children: (state: CmsShellViewerStateSnapshot) => ReactNode;
  mode?: CmsShellViewerMode;
}) {
  if (mode === 'guest') {
    return children({
      currentUser: null,
      hasAuthSessionHint: false,
      isAuthenticated: false,
      isPending: false,
      mode,
      shouldAssumeAuthenticated: false,
    });
  }

  if (mode === 'authenticated') {
    return children({
      currentUser: PREVIEW_AUTHENTICATED_USER,
      hasAuthSessionHint: true,
      isAuthenticated: true,
      isPending: false,
      mode,
      shouldAssumeAuthenticated: true,
    });
  }

  return <CmsShellLiveViewerState>{children}</CmsShellLiveViewerState>;
}

function CmsShellLiveViewerState({
  children,
}: {
  children: (state: CmsShellViewerStateSnapshot) => ReactNode;
}) {
  const { hasAuthSessionHint, isResolved } = useAuthSessionHint();
  const authSession = useAuthSessionObserver();
  const transitionDirection = readAuthSessionTransitionDirection(authSession);
  const transition =
    authSession.transition?.phase === 'pending' ||
    authSession.transition?.phase === 'validating'
      ? authSession.transition
      : null;
  useSyncAuthSessionHintDocumentState(
    isResolved && !hasAuthSessionHint ? 'absent' : null
  );

  if (!isResolved && !transitionDirection) {
    return children({
      currentUser: null,
      hasAuthSessionHint: false,
      isAuthenticated: false,
      isPending: false,
      mode: 'live',
      shouldAssumeAuthenticated: false,
    });
  }

  if (
    !hasAuthSessionHint &&
    !transitionDirection &&
    authSession.status !== 'authenticated'
  ) {
    return children({
      currentUser: null,
      hasAuthSessionHint: false,
      isAuthenticated: false,
      isPending: false,
      mode: 'live',
      shouldAssumeAuthenticated: false,
    });
  }

  return (
    <CmsShellLiveSessionState
      hasAuthSessionHint={hasAuthSessionHint}
      transition={transition}
    >
      {children}
    </CmsShellLiveSessionState>
  );
}

function CmsShellLiveSessionState({
  children,
  hasAuthSessionHint,
  transition,
}: {
  children: (state: CmsShellViewerStateSnapshot) => ReactNode;
  hasAuthSessionHint: boolean;
  transition: AuthSessionTransitionState | null;
}) {
  const { data: session, isPending } = useAuthSession();
  useMarkExternalSignInForValidation(transition);
  const currentUser = (session?.user ?? null) as AuthSessionHintUser | null;
  const isAuthenticated = Boolean(currentUser);
  const transitionDirection = transition?.direction ?? null;
  const transitionTargetReached =
    transitionDirection === 'sign-in'
      ? isAuthenticated
      : transitionDirection === 'sign-out'
        ? !isPending && !isAuthenticated
        : true;
  const isTransitionPending =
    transitionDirection !== null && !transitionTargetReached;
  const effectivePending = isPending || isTransitionPending;
  useSyncAuthSessionHintDocumentState(
    effectivePending || isAuthenticated ? 'present' : 'absent'
  );

  useAuthSessionObserverPublisher({
    status: isPending
      ? 'loading'
      : isAuthenticated
        ? 'authenticated'
        : 'unauthenticated',
    currentUserId: currentUser?.id ?? null,
    enabled: transitionDirection === null || transitionTargetReached,
    source: 'cms-shell-viewer-state',
  });

  return children({
    currentUser: effectivePending ? null : currentUser,
    hasAuthSessionHint,
    isAuthenticated: effectivePending ? false : isAuthenticated,
    isPending: effectivePending,
    mode: 'live',
    shouldAssumeAuthenticated:
      effectivePending || isAuthenticated || (hasAuthSessionHint && isPending),
  });
}
